import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { camelCase_to_Sentence, objectsMatch, toTitleCase } from '@iotv/datamodel-core';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import LockResetIcon from '@mui/icons-material/LockReset';
import PermIdentityIcon from '@mui/icons-material/PermIdentity';
import SaveIcon from '@mui/icons-material/Save';
import { Button, FormLabel, Skeleton, Stack, TextField, ToggleButton } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 } from 'uuid';
import { UserAvatarSelector } from '../../../../../components/User';
import { AutosizedTextField } from '../../../../../components/common/AutosizedTextField';
import { DialogWrapper } from '../../../../../components/common/DiaglogWrapper';
import { ConsumerDao } from '../../../../../data/ConsumerDAO';
import { CustomerDAO, UserDAO } from '../../../../../data/SystemManagementDAO';
import { useApplicationDataQueries } from '../../../../../hooks/useApplicationDataQueries';
import { Severity } from '../../../../../types';
import { commonButtonProps } from './Controls/common';
import { saveAddUserRoles, saveRemoveUserRoles } from './SpecificTypes/userFns';
import useDetailCard from './useDetailCard';
const debug = process.env.REACT_APP_DEBUG && false;
const cognitoPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[\^$*.\[\]{}\(\)?\-“!@#%&/,><\’:;|_~`])\S{6,99}$/;
export const UserDetailCard = (props) => {
    'use no memo';
    // Existing memoization could not be preserved
    // This dependency may be mutated later, which could cause the value to change unexpectedly.
    //const [{ contextUser, isSmallView }] = useContext(AppMainViewContext)
    const { contextUser, isSmallView, allowedActions } = props;
    const editingSelf = props.matchedPrimary.sk === contextUser?.sk;
    // This would usually come from useDetailCard, but because I need it to mutate the transaction functions (which are an input to useDetailCard), I need it now.
    const { isNewObject: isNewUser } = useApplicationDataQueries({ item: props.matchedPrimary });
    /** The most proximate customer might not exist if the user is new */
    const userContextCustomer = props.mostProximateCustomer ?? props.subContextCustomer;
    debug && console.log('UserDetailCard: userContextCustomer', userContextCustomer, props.subContextCustomer);
    /** Used to mutate the transactionFunctions only for existing users */
    const saveExistingUser = async (saveUser) => {
        const saveObj = { ...saveUser };
        if (isNewUser) {
            /**
             * WARN: If we're saving a new user, we're probably saving from a Listerlizer, which mutates transactionFunctions.saveObject on it's own.
             *
             * This function is not called in that case. See {@link UserDetailTransactionFunctions} below
             */
        }
        else {
            /** Try to save user first */
            let updateRes = { err: null, data: null };
            if (contextCurrentlyAdmin) {
                const res = await UserDAO.updateUser({
                    contextCustomer: userContextCustomer,
                    contextUser: contextUser,
                    userToUpdate: saveObj,
                    makeAdministrator: makeAdmin,
                });
                updateRes.err = res.err;
                updateRes.data = res.data?.Item ?? null;
            }
            else {
                updateRes = await ConsumerDao.save(saveObj);
            }
            debug && console.log('UserDetailCard: updateRes', updateRes);
            if (updateRes.err) {
                props.userFunctions.setUserMessage({
                    id: v4(),
                    type: 'MessageBar',
                    severity: Severity.error,
                    label: 'Error saving user',
                    content: `There was an error saving ${saveObj.name}`,
                });
                console.error('UserDetailCard: error while saving user', updateRes);
                return;
            }
            if (!updateRes?.data) {
                props.userFunctions.setUserMessage({
                    id: v4(),
                    type: 'MessageBar',
                    severity: Severity.error,
                    label: 'Error saving user',
                    content: `There was an error saving ${saveObj.name}`,
                });
                console.error('UserDetailCard: no data returned from update user?', updateRes);
                return;
            }
            const userThatHasBeenSaved = updateRes.data;
            // Update roles
            const rolesToRemove = currentRoles?.filter((cr) => !selectedRoles.includes(cr)) ?? [];
            const rolesToAdd = selectedRoles.filter((sr) => !currentRoles?.includes(sr));
            debug && console.log('UserDetailCard: adding roles', rolesToAdd, ', removing roles', rolesToRemove);
            const addRes = await saveAddUserRoles(rolesToAdd, userThatHasBeenSaved);
            debug && console.log('UserDetailCard: add user roles res', addRes);
            const rolesFailedToAdd = addRes.fails.map((f) => f.reason.roleName);
            const rolesAdded = addRes.successes.map((s) => s.value.roleName);
            const removeRes = await saveRemoveUserRoles(rolesToRemove, userThatHasBeenSaved);
            debug && console.log('UserDetailCard: remove user roles res', removeRes);
            const rolesFailedToRemove = removeRes.fails.map((f) => f.reason.roleName);
            const rolesRemoved = removeRes.successes.map((s) => s.value.roleName);
            // Update local detail card stuff
            const oldCurrentRoles = currentRoles ?? [];
            const newCurrentRoles = [...oldCurrentRoles, ...rolesAdded].filter((r) => !rolesRemoved.includes(r));
            setCurrentRoles(newCurrentRoles);
            setUserCurrentlyAdmin(makeAdmin);
            // Add objects to state
            // This also helpfully replaces the matched primary so the buttons grey out because matched primary now matches local object
            // The custom userEditedFn will also work go through because of set states just above here
            props.userFunctions.addObjectsToState([userThatHasBeenSaved]);
            setLocalUser({ ...userThatHasBeenSaved });
            if (editingSelf)
                props.userFunctions.setUser(userThatHasBeenSaved);
            debug && console.log('UserDetailCard: New current roles:', newCurrentRoles);
            // Print message to user
            if (rolesFailedToRemove.length > 0 || rolesFailedToAdd.length > 0) {
                let message = `Updated ${userThatHasBeenSaved.name}, but`;
                if (rolesFailedToAdd.length > 0) {
                    message += ` failed to add roles: ${rolesFailedToAdd.join(', ')}`;
                }
                if (rolesFailedToRemove.length > 0) {
                    if (rolesFailedToAdd.length > 0) {
                        message += `, and`;
                    }
                    message += ` failed to remove roles: ${rolesFailedToRemove.join(', ')}`;
                }
                props.userFunctions.setUserMessage({
                    id: v4(),
                    type: 'MessageBar',
                    severity: Severity.warning,
                    label: 'User saved but some roles failed',
                    content: message,
                });
            }
            else {
                props.userFunctions.setUserMessage({
                    id: v4(),
                    type: 'MessageBar',
                    severity: Severity.success,
                    label: 'User saved',
                    content: `Saved ${userThatHasBeenSaved.name}`,
                });
            }
        }
    };
    /** Roles */
    const [currentRoles, setCurrentRoles] = useState(undefined);
    const [userCurrentlyAdmin, setUserCurrentlyAdmin] = useState(undefined);
    const [contextCurrentlyAdmin, setContextCurrentlyAdmin] = useState(undefined);
    const [makeAdmin, setMakeAdmin] = useState(false);
    // When this state line first runs, isNewUser isn't actually loaded yet, so I have an effect that sets the role.
    const [selectedRoles, setSelectedRoles] = useState(props.requiredRoles ?? []);
    const [possibleRoles, setPossibleRoles] = useState([]);
    useEffect(() => {
        if (isNewUser && !props.requiredRoles) {
            setSelectedRoles(['User']);
        }
        else {
            // nothing to do
        }
    }, [isNewUser]);
    const [queryingRoles, setQueryingRoles] = useState(false);
    const [queryingUserRoles, setQueryingUserRoles] = useState(false);
    const [queryingAdmin, setQueryingAdmin] = useState(false);
    const selectRole = (newRole) => setSelectedRoles((r) => [...r, newRole]);
    const deselectRole = (role) => {
        if ((props.requiredRoles ?? []).includes(role)) {
            // nothing to do
        }
        else {
            setSelectedRoles((r) => [...r].filter((r) => r !== role));
        }
    };
    const doRolesMatch = (oldRoles, newRoles) => {
        if (!oldRoles || !newRoles)
            return true;
        if (oldRoles.length !== newRoles.length) {
            return false;
        }
        for (const role of newRoles) {
            if (!oldRoles.includes(role))
                return false;
        }
        for (const role of oldRoles) {
            if (!newRoles.includes(role))
                return false;
        }
        return true;
    };
    // Populate all possible roles
    useEffect(() => {
        setQueryingRoles(true);
        UserDAO.listAllGroupsQuery().then((userRolesRes) => {
            setQueryingRoles(false);
            if (userRolesRes.data) {
                const { Groups, NextToken } = userRolesRes.data;
                if (NextToken)
                    throw new Error('UserDetailCard: un-implemented iteration required in PreSelectUserRoles');
                if (Groups instanceof Array)
                    setPossibleRoles(Groups.filter((g) => g.GroupName !== 'HyperUser').sort((g1, g2) => (g1.Precedence ?? 999) - (g2.Precedence ?? 999)));
            }
        });
    }, []);
    // Get users current roles + figure out if the user we're editing can admin the customer
    useEffect(() => {
        if (isNewUser)
            return;
        if (!contextUser)
            return;
        if (!userContextCustomer)
            return;
        setQueryingAdmin(true);
        CustomerDAO.canAdminCustomer({ contextUser: props.matchedPrimary, customer: userContextCustomer })
            .then((adminRes) => {
            debug && console.log('UserDetailCard: Admin Res', adminRes);
            setQueryingAdmin(false);
            if (adminRes.data !== undefined) {
                setMakeAdmin(adminRes.data ?? false);
                setUserCurrentlyAdmin(adminRes.data ?? false);
            }
        })
            .catch((err) => {
            console.error('UserDetailCard: error on canAdminCustomer', err);
        });
        CustomerDAO.canAdminCustomer({ contextUser, customer: userContextCustomer })
            .then((adminRes) => {
            debug && console.log('UserDetailCard: Admin Res', adminRes);
            setQueryingAdmin(false);
            if (adminRes.data !== undefined) {
                setContextCurrentlyAdmin(adminRes.data ?? false);
            }
        })
            .catch((err) => {
            console.error('UserDetailCard: error on canAdminCustomer', err);
        });
        setQueryingUserRoles(true);
        UserDAO.listGroupsForUser({ Username: props.matchedPrimary.username })
            .then((listGroupsRes) => {
            setQueryingUserRoles(false);
            debug && console.log('UserDetailCard: Users Current Roles', listGroupsRes);
            if (listGroupsRes?.data?.Groups) {
                const groupNames = listGroupsRes.data.Groups.filter((g) => g.GroupName !== undefined && g.GroupName !== 'HyperUser').map((g) => g.GroupName);
                setSelectedRoles(groupNames);
                setCurrentRoles(groupNames);
            }
        })
            .catch((err) => {
            setQueryingUserRoles(false);
            console.error('UserDetailCard: error on listGroupsForUser', err);
        });
    }, [userContextCustomer?.sk, isNewUser]);
    const resetUser = () => {
        // We null accumulate here but really we can be sure they exist because you can't edit these until the roles and admin status have been fetched
        setSelectedRoles([...(currentRoles ?? props.requiredRoles ?? (isNewUser ? ['User'] : []))]);
        setMakeAdmin(userCurrentlyAdmin ?? false);
    };
    /**
     * WARN: Mutates the saveObject transaction function for an EXISTING user
     *
     * We only mutate if it's NOT a new object. The function to save a NEW user *is* mutated, but not here. Instead, it's in in {@link ../../../factories/Listerlizer/fns/mutateSaveOnTransactionFunctions.ts}
     *
     * Except, it is kind of here, because I need to pull some shennanigans to get everything to save properly.
     */
    const UserDetailTransactionFunctions = useMemo(() => isNewUser
        ? {
            ...props.transactionFunctions,
            // If the user is new, then Listerlizer will have already mutated transaction functions
            // However, I've modified the local object. So we need to strip that stuff out.
            // Look, it's not pretty, but it works. The real problem here is mutating the function at all.
            saveObject: async (localObj) => {
                const saveObj = structuredClone(localObj);
                // Will get removed in listerlizer mutated variant of saveObject
                saveObj.makeAdmin = makeAdmin;
                saveObj.adminCustomer = structuredClone(userContextCustomer);
                saveObj.addToGroups = structuredClone(selectedRoles);
                debug && console.log('UserDetailCard: saving new user (obj contains additions that will be stripped out)', saveObj);
                props.transactionFunctions.saveObject(saveObj);
            },
        }
        : {
            // If the user is not new, use my save function (because we need to update groups n stuff) rather than default
            ...props.transactionFunctions,
            saveObject: saveExistingUser,
        }, [isNewUser, selectedRoles, makeAdmin, userContextCustomer?.sk]);
    const userEditedFn = useCallback((matchedPrimary, local) => {
        if (!(objectsMatch(matchedPrimary, local) && objectsMatch(local, matchedPrimary)))
            return true;
        if (isNewUser) {
            if (!doRolesMatch(selectedRoles, ['User']))
                return true;
        }
        else {
            if (!doRolesMatch(selectedRoles, currentRoles))
                return true;
        }
        if (makeAdmin !== userCurrentlyAdmin)
            return true;
        return false;
    }, [selectedRoles, currentRoles, userCurrentlyAdmin, makeAdmin]);
    /** Admin Controls */
    const [editorCanAdminCustomer, setEditorCanAdminCustomer] = useState(false);
    const canEdit = editingSelf || (editorCanAdminCustomer && allowedActions?.edit === true);
    useEffect(() => {
        debug && console.log('UserDetailCard: updating can admin', userContextCustomer, contextUser);
        if (!userContextCustomer)
            return;
        CustomerDAO.canAdminCustomer({ contextUser: contextUser, customer: userContextCustomer }).then((res) => {
            debug && console.log('UserDetailCard: editing user can Admin Customer', res);
            if (res.data) {
                setEditorCanAdminCustomer(res.data);
            }
        });
    }, [contextUser?.sk, userContextCustomer?.sk]);
    /** Custom buttons - reset password */
    const [passwordForReset, setPasswordForReset] = useState('');
    const [resetPasswordError, setResetPasswordError] = useState(undefined);
    const resetPassword = async (newPassword) => {
        const passwordSet_res = await UserDAO.setUserPassword({ Username: localUser.username, Password: newPassword, UserPoolId: undefined });
        debug && console.log(passwordSet_res);
        if (passwordSet_res.err) {
            setResetPasswordError('Failed to set password. Make sure there are at least 8 characters, including uppercase, lowercase, a number and a special character.');
            //let message = `Failed to reset password: ${makePresentable(passwordSet_res.err.message)}`
            //props.userFunctions.setUserMessage({
            //  id: v4(),
            //  label: 'Failed to reset password',
            //  content: ,
            //  type: 'MessageBar',
            //  severity: Severity.error,
            //})
            return false;
        }
        return true;
    };
    const buttonFns = [
        ...(canEdit && !isNewUser
            ? [
                () => {
                    return (_jsx(Button, { ...commonButtonProps,
                        sx: { ...commonButtonProps.sx, maxWidth: 200 },
                        startIcon: _jsx(LockResetIcon, {}),
                        color: 'warning',
                        onClick: () => setResetPasswordDialogOpen(true), children: "Reset Password" }, "resetPassword"));
                },
                () => {
                    return (_jsx(Button, { ...commonButtonProps,
                        sx: { ...commonButtonProps.sx, maxWidth: 120 },
                        startIcon: _jsx(InsertPhotoIcon, {}),
                        onClick: () => setAvatarSelectorDialogOpen(true), children: "Set Photo" }, "avatarSelector"));
                },
            ]
            : []),
    ];
    const [userValidationMap, setUserValidationMap] = useState({});
    const { localObject: localUser, setLocalObject: setLocalUser, isNewObject, detailCardComponents, } = useDetailCard({
        ...props,
        allowedActions: { ...allowedActions, edit: canEdit },
        transactionFunctions: UserDetailTransactionFunctions,
        matchedPrimary: props.matchedPrimary,
        resetCallback: resetUser,
        hasBeenEditedFn: userEditedFn,
        customButtonFns: buttonFns,
        injectedValidationMap: userValidationMap,
    });
    // Validation
    useEffect(() => {
        const newValMap = {};
        if ((localUser.username ?? '').trim() === '') {
            newValMap['username'] = { type: 'required', content: 'Username is invalid' };
        }
        // Password validation should only happen for NEW USERS because temporaryPassword isn't usually set on user objects after the fact
        if (isNewObject && (localUser.temporaryPassword ?? '').trim() !== '' && !new RegExp(cognitoPasswordRegex).test(localUser.temporaryPassword)) {
            newValMap['password'] = { type: 'error', content: 'Password requires an upper case, lower case, number, and a symbol' };
        }
        setUserValidationMap(newValMap);
    }, [localUser.username, localUser.temporaryPassword]);
    const [rolesDialogOpen, setRolesDialogOpen] = useState(false);
    const [passwordResetDialogOpen, setResetPasswordDialogOpen] = useState(false);
    const [avatarSelectorDialogOpen, setAvatarSelectorDialogOpen] = useState(false);
    const handleUserSetupChange = (event, key) => {
        const newVal = event.target.value;
        if (key === 'firstName') {
            const new_full_name = `${newVal} ${localUser.lastName ?? localUser.last_name ?? ''}`;
            setLocalUser({ ...localUser, [key]: newVal, name: new_full_name });
        }
        else if (key === 'lastName') {
            const new_full_name = `${localUser.firstName ?? localUser.first_name ?? ''} ${newVal}`;
            setLocalUser({ ...localUser, [key]: newVal, name: new_full_name });
        }
        else {
            setLocalUser({ ...localUser, [key]: newVal });
        }
    };
    return (_jsxs(Stack, { gap: 1, children: [detailCardComponents.controls, _jsxs(Stack, { direction: isSmallView ? 'column' : 'row', flexWrap: 'wrap', alignItems: isSmallView ? undefined : 'center', gap: 1, children: [_jsxs(Stack, { gap: 1, children: [_jsx(FormLabel, { children: "Name" }), _jsxs(Stack, { direction: isSmallView ? 'column' : 'row', gap: 1, children: [_jsx(AutosizedTextField, { label: 'First name', value: localUser.firstName ?? localUser.first_name ?? '', onChange: (e) => handleUserSetupChange(e, 'firstName'), disabled: !canEdit }, "firstName"), _jsx(AutosizedTextField, { label: 'Last name', value: localUser.lastName ?? localUser.last_name ?? '', onChange: (e) => handleUserSetupChange(e, 'lastName'), disabled: !canEdit }, "lastName")] })] }, "name"), _jsxs(Stack, { gap: 1, children: [_jsx(FormLabel, { children: "Contact" }), _jsxs(Stack, { direction: isSmallView ? 'column' : 'row', gap: 1, children: [_jsx(AutosizedTextField, { label: 'Email address', value: localUser.email ?? '', onChange: (e) => handleUserSetupChange(e, 'email'), disabled: !canEdit }, "email"), _jsx(AutosizedTextField, { label: 'Phone number', value: localUser.phoneNumber ?? localUser.phone_number ?? '', onChange: (e) => handleUserSetupChange(e, 'phoneNumber'), disabled: !canEdit }, "phoneNumber")] })] }, "contact"), _jsxs(Stack, { gap: 1, children: [_jsx(FormLabel, { children: "Authentication" }), _jsxs(Stack, { direction: isSmallView ? 'column' : 'row', gap: 1, children: [_jsx(AutosizedTextField, { label: 'Username', value: localUser.username ?? '', onChange: (e) => handleUserSetupChange(e, 'username'), disabled: !canEdit || !isNewUser, error: userValidationMap['username'] !== undefined && userValidationMap['username'].type !== 'required' }, "username"), isNewUser && (_jsx(AutosizedTextField, { label: 'Password', value: localUser.temporaryPassword ?? '', onChange: (e) => handleUserSetupChange(e, 'temporaryPassword'), error: userValidationMap['password'] !== undefined && userValidationMap['password'].type !== 'required' }, "password"))] })] }, "authentication"), _jsx(Stack, { marginBlockEnd: 0.25, alignSelf: isSmallView ? undefined : 'flex-end', direction: isSmallView ? 'column' : 'row', children: props.showRoleSelection !== false && (_jsxs(Button, { startIcon: _jsx(PermIdentityIcon, {}), onClick: () => setRolesDialogOpen(true), ...commonButtonProps, size: "medium", sx: { backgroundColor: '#4CAF50' }, disabled: !editorCanAdminCustomer, children: ["Roles (", selectedRoles.length, ")"] })) }, "roles")] }), _jsx(DialogWrapper, { slotProps: { paper: { sx: { minHeight: '65dvh', minWidth: '40dvw' } } }, open: rolesDialogOpen, fullWidth: false, header: `${isNewUser ? (localUser.name ?? 'User') : (localUser.name ?? contextUser?.name ?? 'User')}'s Roles`, setClosed: () => setRolesDialogOpen(false), children: _jsx(Stack, { gap: 1, children: queryingRoles ? (_jsx(Skeleton, { variant: "rounded", width: "100%", height: "50dvh" })) : (_jsxs(_Fragment, { children: [_jsx(ToggleButton, { value: 'Admin', selected: makeAdmin, onChange: () => setMakeAdmin((old) => !old), disabled: queryingAdmin || editingSelf, color: 'warning', sx: { textTransform: 'none' }, children: "Administrator" }, "admin"), possibleRoles
                                .filter((group) => group.GroupName !== undefined)
                                .map((groupDef) => {
                                const GroupName = groupDef.GroupName;
                                return (_jsxs(ToggleButton, { value: GroupName, selected: selectedRoles.includes(GroupName), onChange: () => {
                                        if (selectedRoles.includes(GroupName)) {
                                            deselectRole(GroupName);
                                        }
                                        else {
                                            selectRole(GroupName);
                                        }
                                    }, disabled: queryingUserRoles, color: 'info', sx: { textTransform: 'none' }, children: [toTitleCase(camelCase_to_Sentence(GroupName)), " ", props.requiredRoles?.includes(GroupName) ? '(Required)' : ''] }, GroupName));
                            })] })) }) }), _jsx(DialogWrapper, { open: passwordResetDialogOpen, header: 'Reset Password', action: 'Reset Password', actionIcon: _jsx(SaveIcon, {}), setClosed: (submitted) => {
                    if (submitted) {
                        resetPassword(passwordForReset).then((res) => {
                            if (res === true) {
                                props.userFunctions.setUserMessage({
                                    id: v4(),
                                    label: 'Successfully reset user password',
                                    content: 'Successfully reset password',
                                    type: 'SnackBar',
                                    severity: Severity.success,
                                });
                                setResetPasswordDialogOpen(false);
                                setResetPasswordError(undefined);
                                setPasswordForReset('');
                            }
                        });
                    }
                    else {
                        setResetPasswordError(undefined);
                        setResetPasswordDialogOpen(false);
                        setPasswordForReset('');
                    }
                }, children: _jsx(TextField, { id: "new-password", label: "New Password", value: passwordForReset, onChange: (e) => setPasswordForReset(e.target.value), fullWidth: true, sx: { marginBlockStart: 1 }, helperText: resetPasswordError || 'Must be at least 8 characters, including uppercase, lowercase, number and a special character.', error: !!resetPasswordError }) }), _jsx(DialogWrapper, { open: avatarSelectorDialogOpen, header: 'Set Photo', setClosed: () => setAvatarSelectorDialogOpen(false), fullWidth: false, children: _jsx(UserAvatarSelector, { contextUser: contextUser, subjectUser: localUser, userFunctions: props.userFunctions, transactionFunctions: props.transactionFunctions }) }), detailCardComponents.table] }));
};
